import { userAddressService } from '@/services/userAddresses';
import { getCurrentUser, MAX_SAVED_ADDRESSES_GUEST, saveToStorage } from './location-storage';
import { loadUserAddresses } from './location-db';
import type { SavedAddress, LocationState } from './location.types';

export const addSavedAddress = async (
  addressData: Omit<SavedAddress, 'id' | 'createdAt' | 'isDefault'>,
  state: LocationState,
  setState: (updates: Partial<LocationState>) => void
) => {
  try {
    const user = await getCurrentUser();
    
    if (user) {
      // 로그인 사용자: 데이터베이스에 저장
      const result = await userAddressService.addAddress(user.id, {
        alias: addressData.alias,
        address: addressData.address,
        detail_address: addressData.detailAddress,
        latitude: addressData.coordinate.latitude,
        longitude: addressData.coordinate.longitude
      });
      
      if (result.success && result.data) {
        // DB에서 저장된 주소를 로컬 상태에 추가
        const dbAddress = result.data;
        const newAddress: SavedAddress = {
          id: dbAddress.id,
          alias: dbAddress.alias,
          address: dbAddress.address,
          jibunAddress: dbAddress.address, // DB에는 도로명주소만 저장
          detailAddress: dbAddress.detail_address || '',
          deliveryInstructions: addressData.deliveryInstructions || '',
          additionalInfo: addressData.additionalInfo || '',
          coordinate: {
            latitude: dbAddress.latitude || 0,
            longitude: dbAddress.longitude || 0
          },
          isDefault: dbAddress.is_default,
          createdAt: dbAddress.created_at
        };
        
        setState({
          savedAddresses: [...state.savedAddresses, newAddress]
        });
        
        // 첫 번째 주소이거나 선택된 주소라면 currentLocation 업데이트
        if (dbAddress.is_default) {
          await selectAddress(newAddress.id, state, setState);
        }
      } else {
        console.error('Failed to add address to database:', result.error);
        throw new Error(result.error || '주소 저장에 실패했습니다.');
      }
    } else {
      // 게스트 사용자: localStorage 사용 (기존 로직)
      if (state.savedAddresses.length >= MAX_SAVED_ADDRESSES_GUEST) {
        // 가장 오래된 주소 제거 (FIFO)
        const oldestAddress = state.savedAddresses.reduce((oldest, current) => 
          oldest.createdAt < current.createdAt ? oldest : current
        );
        await removeSavedAddress(oldestAddress.id, state, setState);
      }
      
      const newAddress: SavedAddress = {
        ...addressData,
        id: `addr_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        createdAt: new Date().toISOString(),
        isDefault: false
      };
      
      const newState = {
        savedAddresses: [...state.savedAddresses, newAddress]
      };
      setState(newState);
      await saveToStorage({
        currentLocation: state.currentLocation,
        savedAddresses: newState.savedAddresses,
        selectedAddressId: state.selectedAddressId
      });
    }
  } catch (error) {
    console.error('Failed to add address:', error);
    setState({ 
      locationError: error instanceof Error ? error.message : '주소 저장에 실패했습니다.' 
    });
  }
};

export const updateSavedAddress = async (
  id: string,
  updates: Partial<SavedAddress>,
  state: LocationState,
  setState: (updates: Partial<LocationState>) => void
) => {
  try {
    const user = await getCurrentUser();
    
    if (user) {
      // 로그인 사용자: 데이터베이스 업데이트
      const dbUpdates: any = {};
      if (updates.alias) dbUpdates.alias = updates.alias;
      if (updates.address) dbUpdates.address = updates.address;
      if (updates.detailAddress) dbUpdates.detail_address = updates.detailAddress;
      if (updates.coordinate) {
        dbUpdates.latitude = updates.coordinate.latitude;
        dbUpdates.longitude = updates.coordinate.longitude;
      }
      
      const result = await userAddressService.updateAddress(user.id, id, dbUpdates);
      
      if (result.success) {
        // 로컬 상태 업데이트
        setState({
          savedAddresses: state.savedAddresses.map(addr =>
            addr.id === id ? { ...addr, ...updates } : addr
          )
        });
      } else {
        console.error('Failed to update address in database:', result.error);
        throw new Error(result.error || '주소 수정에 실패했습니다.');
      }
    } else {
      // 게스트 사용자: localStorage 사용 (기존 로직)
      const newState = {
        savedAddresses: state.savedAddresses.map(addr =>
          addr.id === id ? { ...addr, ...updates } : addr
        )
      };
      setState(newState);
      await saveToStorage({
        currentLocation: state.currentLocation,
        savedAddresses: newState.savedAddresses,
        selectedAddressId: state.selectedAddressId
      });
    }
  } catch (error) {
    console.error('Failed to update address:', error);
    setState({ 
      locationError: error instanceof Error ? error.message : '주소 수정에 실패했습니다.' 
    });
  }
};

export const removeSavedAddress = async (
  id: string,
  state: LocationState,
  setState: (updates: Partial<LocationState>) => void
) => {
  const isSelectedAddress = state.selectedAddressId === id;
  
  try {
    const user = await getCurrentUser();
    
    if (user) {
      // 로그인 사용자: 데이터베이스에서 삭제
      const result = await userAddressService.deleteAddress(user.id, id);
      
      if (result.success) {
        // 로컬 상태 업데이트
        setState({
          savedAddresses: state.savedAddresses.filter(addr => addr.id !== id),
          selectedAddressId: isSelectedAddress ? null : state.selectedAddressId,
          // 선택된 주소가 삭제되면 currentLocation도 초기화
          currentLocation: isSelectedAddress 
            ? { coordinate: null, address: null }
            : state.currentLocation
        });
        
        // 선택된 주소가 삭제되고 다른 주소가 자동 선택된 경우 새로고침
        if (isSelectedAddress) {
          const addressData = await loadUserAddresses(user.id);
          setState({
            savedAddresses: addressData.savedAddresses,
            selectedAddressId: addressData.selectedAddressId,
            currentLocation: addressData.currentLocation
          });
        }
      } else {
        console.error('Failed to delete address from database:', result.error);
        throw new Error(result.error || '주소 삭제에 실패했습니다.');
      }
    } else {
      // 게스트 사용자: localStorage 사용 (기존 로직)
      const newState = {
        savedAddresses: state.savedAddresses.filter(addr => addr.id !== id),
        selectedAddressId: isSelectedAddress ? null : state.selectedAddressId,
        // 선택된 주소가 삭제되면 currentLocation도 초기화
        currentLocation: isSelectedAddress 
          ? { coordinate: null, address: null }
          : state.currentLocation
      };
      setState(newState);
      await saveToStorage({
        currentLocation: newState.currentLocation,
        savedAddresses: newState.savedAddresses,
        selectedAddressId: newState.selectedAddressId
      });
    }
  } catch (error) {
    console.error('Failed to remove address:', error);
    setState({ 
      locationError: error instanceof Error ? error.message : '주소 삭제에 실패했습니다.' 
    });
  }
};

export const selectAddress = async (
  id: string,
  state: LocationState,
  setState: (updates: Partial<LocationState>) => void
) => {
  const selectedAddress = state.savedAddresses.find(addr => addr.id === id);
  
  if (!selectedAddress) return;
  
  try {
    const user = await getCurrentUser();
    
    if (user) {
      // 로그인 사용자: 데이터베이스에서 선택 상태 업데이트
      const result = await userAddressService.selectAddress(user.id, id);
      
      if (result.success) {
        // 로컬 상태 업데이트
        setState({
          savedAddresses: state.savedAddresses.map(addr => ({
            ...addr,
            isDefault: addr.id === id
          })),
          selectedAddressId: id,
          // 선택된 주소를 currentLocation에 반영 (상세주소 포함)
          currentLocation: {
            coordinate: selectedAddress.coordinate,
            address: {
              roadAddress: selectedAddress.detailAddress 
                ? `${selectedAddress.address} ${selectedAddress.detailAddress}`
                : selectedAddress.address,
              jibunAddress: selectedAddress.detailAddress
                ? `${selectedAddress.jibunAddress} ${selectedAddress.detailAddress}`
                : selectedAddress.jibunAddress,
              zonecode: selectedAddress.additionalInfo
            }
          }
        });
      } else {
        console.error('Failed to select address in database:', result.error);
        throw new Error(result.error || '주소 선택에 실패했습니다.');
      }
    } else {
      // 게스트 사용자: localStorage 사용 (기존 로직)
      const newState = {
        savedAddresses: state.savedAddresses.map(addr => ({
          ...addr,
          isDefault: addr.id === id
        })),
        selectedAddressId: id,
        // 선택된 주소를 currentLocation에 반영 (상세주소 포함)
        currentLocation: {
          coordinate: selectedAddress.coordinate,
          address: {
            roadAddress: selectedAddress.detailAddress 
              ? `${selectedAddress.address} ${selectedAddress.detailAddress}`
              : selectedAddress.address,
            jibunAddress: selectedAddress.detailAddress
              ? `${selectedAddress.jibunAddress} ${selectedAddress.detailAddress}`
              : selectedAddress.jibunAddress,
            zonecode: selectedAddress.additionalInfo
          }
        }
      };
      setState(newState);
      await saveToStorage({
        currentLocation: newState.currentLocation,
        savedAddresses: newState.savedAddresses,
        selectedAddressId: newState.selectedAddressId
      });
    }
  } catch (error) {
    console.error('Failed to select address:', error);
    setState({ 
      locationError: error instanceof Error ? error.message : '주소 선택에 실패했습니다.' 
    });
  }
};

export const clearSelectedAddress = async (
  state: LocationState,
  setState: (updates: Partial<LocationState>) => void
) => {
  const newState = {
    savedAddresses: state.savedAddresses.map(addr => ({
      ...addr,
      isDefault: false
    })),
    selectedAddressId: null,
    // 선택 해제 시 currentLocation도 초기화
    currentLocation: {
      coordinate: null,
      address: null
    }
  };
  setState(newState);
  await saveToStorage({
    currentLocation: newState.currentLocation,
    savedAddresses: newState.savedAddresses,
    selectedAddressId: newState.selectedAddressId
  });
};